import { InvoicePosition, VATSummary, VetdataInvoiceData } from '@/types/LieferantRechnungFormularDtos';

export class VetdataXmlGenerator {
    // Main entry point
    public generateXml(invoiceData: VetdataInvoiceData): string {
        const vatSummary = this.calculateVATSummary(invoiceData.positions);

        return `<?xml version="1.0" encoding="utf-8"?>
<VetdataInvoice xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="vetdata_1.0.xsd">
${this.buildBillerSection(invoiceData)}
${this.buildCustomerSection(invoiceData)}
${this.buildGeneralInformationSection(invoiceData)}
${this.buildPaymentInformationSection(invoiceData)}
${this.buildVATInformationSections(vatSummary)}
${this.buildTotalInvoiceAmountsSection(invoiceData, vatSummary)}
${this.buildLineItemsSections(invoiceData.positions)}
</VetdataInvoice>`;
    }

    // Helper: Build Biller section
    private buildBillerSection(data: VetdataInvoiceData): string {
        const { formData, supplierInfo } = data;

        // Build address lines (max 3)
        const addressLines = [supplierInfo.adresszeile1, supplierInfo.adresszeile2, supplierInfo.adresszeile3]
            .filter((line) => line && line.trim() !== '')
            .map((line) => `        <Address>${this.escapeXml(line!)}</Address>`)
            .join('\n');

        return `    <Biller>
        <DocumentType>${this.escapeXml(formData.documentType)}</DocumentType>
        <DocumentIdentifier>${this.escapeXml(formData.documentIdentifier)}</DocumentIdentifier>
        <DocumentIssueDate>${this.formatDate(formData.documentIssueDate)}</DocumentIssueDate>
        <DocumentCurrency>CHF</DocumentCurrency>
        <TVSSupplierNumber>${supplierInfo.tvsLieferantennummer}</TVSSupplierNumber>
        <TaxIdentificationNumber>${this.escapeXml(supplierInfo.mwstNummer)}</TaxIdentificationNumber>
        <BillerAddress>
            <LegalName>${this.escapeXml(supplierInfo.firma)}</LegalName>
${addressLines}
            <CityName>${this.escapeXml(supplierInfo.ort)}</CityName>
            <ZipCode>${supplierInfo.plz}</ZipCode>
            <Country>${this.escapeXml(supplierInfo.land)}</Country>
        </BillerAddress>
    </Biller>`;
    }

    // Helper: Build Customer section
    private buildCustomerSection(data: VetdataInvoiceData): string {
        const { formData } = data;

        // Build address lines (max 3)
        const addressLines = [formData.address1, formData.address2, formData.address3]
            .filter((line) => line && line.trim() !== '')
            .map((line) => `            <Address>${this.escapeXml(line!)}</Address>`)
            .join('\n');

        return `    <Customer>
        <TVSCustomerNumber>${this.escapeXml(formData.tvsCustomerNumber)}</TVSCustomerNumber>
        <CustomerNumber>${this.escapeXml(formData.customerNumber)}</CustomerNumber>
        <CustomerAddress>
            <LegalName>${this.escapeXml(formData.companyName)}</LegalName>
${addressLines}
            <CityName>${this.escapeXml(formData.cityName)}</CityName>
            <ZipCode>${this.escapeXml(formData.zipCode)}</ZipCode>
            <Country>${this.escapeXml(formData.country)}</Country>
        </CustomerAddress>
    </Customer>`;
    }

    // Helper: Build GeneralInformation section
    private buildGeneralInformationSection(data: VetdataInvoiceData): string {
        const { formData } = data;

        // OrderReference and OrderDate are optional - only include if present
        const orderReference = formData.orderReference ? `        <OrderReference>${this.escapeXml(formData.orderReference)}</OrderReference>` : '';
        const orderDate = formData.orderDate ? `        <OrderDate>${this.formatDate(formData.orderDate)}</OrderDate>` : '';

        return `    <GeneralInformation>
        <DeliveryDate>${this.formatDate(formData.deliveryDate)}</DeliveryDate>
${orderReference}
${orderDate}
    </GeneralInformation>`;
    }

    // Helper: Build PaymentInformation section
    private buildPaymentInformationSection(data: VetdataInvoiceData): string {
        // For now, we're setting PayableByTVS to true (1) by default
        // In a real implementation, this might come from supplier settings or form data
        return `    <PaymentInformation>
        <PayableByTVS>1</PayableByTVS>
    </PaymentInformation>`;
    }

    // Helper: Build VATInformation sections (one per VAT rate)
    private buildVATInformationSections(vatSummary: VATSummary[]): string {
        return vatSummary
            .map(
                (vat) => `    <VATInformation>
        <VATPercentageRate>${vat.vatRate.toFixed(2)}</VATPercentageRate>
        <TaxableAmount>${vat.taxableAmount.toFixed(2)}</TaxableAmount>
        <TaxAmountPayable>${vat.taxAmountPayable.toFixed(2)}</TaxAmountPayable>
    </VATInformation>`
            )
            .join('\n');
    }

    // Helper: Build TotalInvoiceAmounts section
    private buildTotalInvoiceAmountsSection(data: VetdataInvoiceData, vatSummary: VATSummary[]): string {
        const totalVAT = vatSummary.reduce((sum, vat) => sum + vat.taxAmountPayable, 0);

        return `    <TotalInvoiceAmounts>
        <TotalAmountExclVAT>${data.totalTaxableAmount.toFixed(2)}</TotalAmountExclVAT>
        <TotalAmountInclVAT>${data.totalInclVat.toFixed(2)}</TotalAmountInclVAT>
        <TotalTaxAmountPayable>${totalVAT.toFixed(2)}</TotalTaxAmountPayable>
        <RoundingAmount>${data.formData.roundingAmount.toFixed(2)}</RoundingAmount>
    </TotalInvoiceAmounts>`;
    }

    // Helper: Build LineItem sections (one per position)
    private buildLineItemsSections(positions: InvoicePosition[]): string {
        return positions
            .map(
                (pos) => `    <LineItem>
        <SupplierArticleNumber>${this.escapeXml(pos.SupplierArticleNumber)}</SupplierArticleNumber>
${pos.VetpointArticleNumber ? `        <VetpointArticleNumber>${this.escapeXml(pos.VetpointArticleNumber)}</VetpointArticleNumber>` : ''}
${pos.gtin ? `        <EAN>${this.escapeXml(pos.gtin)}</EAN>` : ''}
        <Description>${this.escapeXml(pos.productName)}</Description>
        <UnitOfMeasurement>${this.escapeXml(pos.unitOfMeasurement)}</UnitOfMeasurement>
        <InvoicedQuantity>${pos.quantity!.toFixed(3)}</InvoicedQuantity>
        <PricePerUnitExcl>${pos.unitPrice!.toFixed(2)}</PricePerUnitExcl>
        <VATPercentageRate>${pos.vatRate.toFixed(2)}</VATPercentageRate>
        <TotalAmountExclVAT>${pos.taxableAmount!.toFixed(2)}</TotalAmountExclVAT>
        <VATAmountPayable>${pos.taxAmountPayable!.toFixed(2)}</VATAmountPayable>
    </LineItem>`
            )
            .join('\n');
    }

    // Helper: Calculate VAT summary grouped by rate
    private calculateVATSummary(positions: InvoicePosition[]): VATSummary[] {
        const vatMap = new Map<number, VATSummary>();

        positions.forEach((position) => {
            const rate = position.vatRate;
            const existing = vatMap.get(rate);

            if (existing) {
                existing.taxableAmount += position.taxableAmount || 0;
                existing.taxAmountPayable += position.taxAmountPayable || 0;
            } else {
                vatMap.set(rate, {
                    vatRate: rate,
                    taxableAmount: position.taxableAmount || 0,
                    taxAmountPayable: position.taxAmountPayable || 0
                });
            }
        });

        // Round values to 2 decimals and return as array
        const vatSummary = Array.from(vatMap.values()).map((item) => ({
            vatRate: item.vatRate,
            taxableAmount: Number(item.taxableAmount.toFixed(2)),
            taxAmountPayable: Number(item.taxAmountPayable.toFixed(2))
        }));

        return vatSummary;
    }

    // Helper: Format date to YYYY-MM-DD
    private formatDate(dateString: string): string {
        if (!dateString) {
            return '';
        }

        // Handle different date formats
        // Expected input format: DD.MM.YYYY (from NowhowDatepicker with de-CH locale)
        const parts = dateString.split('.');

        if (parts.length === 3) {
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];
            return `${year}-${month}-${day}`;
        }

        // Fallback: try to parse as Date
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }

        // If all else fails, return the original string
        return dateString;
    }

    // Helper: XML escape special characters
    private escapeXml(text: string): string {
        if (!text) {
            return '';
        }

        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
    }
}
